import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { getToken, clearToken } from '@/services/auth/auth-service'
import { env } from '@/utils/env'

/**
 * Create and configure the Axios instance
 */
const axiosInstance: AxiosInstance = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: env.apiTimeout,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
    }
})

/**
 * Request interceptor
 * Adds Bearer token from AuthService if available
 */
axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        try {
            const authToken = await getToken()
            if (authToken && config.headers) {
                config.headers.Authorization = `Bearer ${authToken.token}`
            }
        } catch (error) {
            console.error('[API] Failed to retrieve token for request', error)
            // Continue with request without token
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

/**
 * Response interceptor
 * Handles 401 errors and network failures
 */
axiosInstance.interceptors.response.use(
    response => {
        return response
    },
    async error => {
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            try {
                await clearToken()
            } catch (clearError) {
                console.error('[API] Failed to clear token on 401', clearError)
            }
        }

        return Promise.reject(error)
    }
)

export default axiosInstance
